import * as React from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import routes from '../routes';
import { RootState } from '../reducers/reducers';
import { selectAppSettings, selectShouldDisplayAsMdm, selectShouldDisplayAsMdmByAppId } from '../selectors/selectors';
import { AppRouteComponentMatchParams } from '../typescriptHelpers';
import { AppPlatform, AppStoreType } from '../types';
import { displayTextForAppPlatform } from './displayHelpers';
import Text from 'antd/lib/typography/Text';
import workflowsPlatformAndroidMdm from '../../assets/workflows-platform-android-mdm.svg';

// Apps with the ShouldDisplayAsMDM app config (toggled in Runway Admin) are on a real platform (e.g.
// react-native-ota) but are distributed via MDM, so we display them as "MDM" (label + icon).
// Display-only: no AppPlatform, no release logic.

export interface MdmPlatformOverride {
    label: string;
    icon: (size: number) => React.ReactNode;
}

export function getMdmPlatformOverride(shouldDisplayAsMdm: boolean | null | undefined): MdmPlatformOverride | null {
    if (!shouldDisplayAsMdm) {
        return null;
    }

    return {
        label: 'MDM',
        icon: (size) => (
            <img src={workflowsPlatformAndroidMdm} width={size} height={size} style={{ flexShrink: 0 }} alt="" />
        ),
    };
}

// For app records that already carry the MDM flag (e.g. UserAppData), so no store lookup is needed.
export function platformLabelForApp(app: {
    platform: AppPlatform;
    appStoreType?: AppStoreType;
    shouldDisplayAsMdm?: boolean;
}): string {
    return (
        getMdmPlatformOverride(app.shouldDisplayAsMdm)?.label ??
        displayTextForAppPlatform(app.platform, app.appStoreType)
    );
}

// Apps with the ShouldOmitPlatformFromAppName app config show their name without the "(Platform)" suffix.
export function platformLabelForAppName(app: {
    platform: AppPlatform;
    appStoreType?: AppStoreType;
    shouldDisplayAsMdm?: boolean;
    shouldOmitPlatformFromAppName?: boolean;
}): string | null {
    return app.shouldOmitPlatformFromAppName ? null : platformLabelForApp(app);
}

export function appNameWithPlatform(appName: string, platformLabel: string | null | undefined): string {
    return platformLabel ? `${appName} (${platformLabel})` : appName;
}

export const AppNameWithSecondaryPlatform: React.FC<{ appName: string; platformLabel: string | null }> = ({
    appName,
    platformLabel,
}) => (
    <>
        {appName}
        {platformLabel && (
            <>
                {' '}
                <Text type="secondary">({platformLabel})</Text>
            </>
        )}
    </>
);

// Falls back to the app in the current route when no appId is given.
export function useAppNameWithPlatform(appId?: string): string {
    const routeAppId = useRouteMatch<AppRouteComponentMatchParams>(routes.app)?.params?.appIdParam;
    const resolvedAppId = appId ?? routeAppId;
    const appSettings = useSelector((state: RootState) =>
        resolvedAppId ? selectAppSettings(state, resolvedAppId) : undefined,
    );
    if (!appSettings) {
        return '';
    }
    return appNameWithPlatform(appSettings.appName, platformLabelForAppName(appSettings));
}

// Falls back to the app in the current route when no appId is given.
export function useMdmPlatformOverride(appId?: string): MdmPlatformOverride | null {
    const routeAppId = useRouteMatch<AppRouteComponentMatchParams>(routes.app)?.params?.appIdParam;
    const resolvedAppId = appId ?? routeAppId;
    const shouldDisplayAsMdm = useSelector((state: RootState) =>
        resolvedAppId ? selectShouldDisplayAsMdm(state, resolvedAppId) : false,
    );
    return getMdmPlatformOverride(shouldDisplayAsMdm);
}

// For call sites that resolve the override for many apps in one render (loops, memoized maps).
export function useMdmPlatformOverrideResolver(): (appId?: string) => MdmPlatformOverride | null {
    const routeAppId = useRouteMatch<AppRouteComponentMatchParams>(routes.app)?.params?.appIdParam;
    const shouldDisplayAsMdmByAppId = useSelector(selectShouldDisplayAsMdmByAppId, shallowEqual);
    return React.useCallback(
        (appId) => {
            const resolvedAppId = appId ?? routeAppId;
            return getMdmPlatformOverride(resolvedAppId ? shouldDisplayAsMdmByAppId[resolvedAppId] : false);
        },
        [shouldDisplayAsMdmByAppId, routeAppId],
    );
}
